/** Cache-only family reads for Capture and Home: no transport in either render path. */
import { liveQuery } from "dexie";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { db, familyDb } from "@/lib/db";
import type { FamilyView } from "@/lib/backend";
import { familyLedger } from "@/lib/ledger";
import { FAMILY_VIEW_KEY, FAMILY_REMOVED_KEY, countPending, getFamilySyncStatus, subscribeFamilySync } from "@/lib/sync";
import { isSavingsCategory } from "@/domain/category_kind";
import { periodBounds } from "@/domain/period";
import { parseFamilyView } from "@/lib/family";
import { computeStandings } from "./use_data";

const HOUR_MS = 3_600_000;

async function readFamilyData() {
  try {
    const cache = await db.meta.get(FAMILY_VIEW_KEY);
    if (!cache || await familyDb.meta.get(FAMILY_REMOVED_KEY)) return null;
    const family: FamilyView = parseFamilyView(JSON.parse(cache.value));
    const ledger = familyLedger(family.family_id);
    const [profile, categories, expenses, budgets, pending, personalLast, personalPending] = await Promise.all([
      familyDb.profile.get(family.family_id),
      familyDb.categories.orderBy("sortOrder").filter(c => c.deletedAt === null && !isSavingsCategory(c)).toArray(),
      familyDb.expenses.filter(e => e.deletedAt === null).toArray(),
      familyDb.budgets.filter(b => b.deletedAt === null).toArray(), countPending(ledger),
      db.expenses.orderBy("occurredAt").reverse().filter(e => e.deletedAt === null).limit(1).toArray(), countPending(),
    ]);
    const familyLast = await familyDb.expenses.orderBy("occurredAt").reverse()
      .filter(e => e.deletedAt === null && (e._createdBy === family.my_member_id || (!e._createdBy && e._dirty === 1)))
      .limit(1).toArray();
    const last = [...familyLast, ...personalLast].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))[0]?.categoryId ?? null;
    const lastChanged = [profile, ...budgets].filter(row => row !== undefined).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
    return {
      family, ledger, profileMissing: !profile,
      lastChangedAt: lastChanged?.updatedAt,
      lastChangedBy: lastChanged?._dirty ? family.my_member_id : lastChanged?._updatedBy,
      familyError: !profile && getFamilySyncStatus().lastSyncedAt ? "Family settings haven't loaded" : null,
      profile, allCategories: categories, categories: categories.filter(c => c.archivedAt === null), expenses, budgets, pending, personalPending, last,
    };
  } catch (cause) {
    return {
      family: null, ledger: undefined, profile: undefined, profileMissing: false,
      allCategories: [], categories: [], expenses: [], budgets: [], pending: 0, personalPending: 0, last: null,
      lastChangedAt: undefined, lastChangedBy: undefined,
      familyError: `Family data couldn't be loaded: ${cause instanceof Error ? cause.message : "Please try again."}`,
    };
  }
}
// One cache subscription across every mounted screen, released on the last unmount.
type Data = Awaited<ReturnType<typeof readFamilyData>>;
let snapshot: Data | undefined;
const listeners = new Set<() => void>();
let subscription: { unsubscribe(): void } | undefined;
function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!subscription) subscription = liveQuery(readFamilyData).subscribe(value => {
    snapshot = value; listeners.forEach(notify => notify());
  });
  return () => { listeners.delete(listener); if (!listeners.size) { subscription?.unsubscribe(); subscription = undefined; snapshot = undefined; } };
}
export function useFamilyData() {
  const data = useSyncExternalStore(subscribe, () => snapshot);
  const [status, setStatus] = useState(getFamilySyncStatus);
  useEffect(() => subscribeFamilySync(setStatus), []);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const timer = setInterval(() => setNow(new Date()), HOUR_MS); return () => clearInterval(timer); }, []);
  return useMemo(() => {
    if (data === undefined) return undefined;
    if (data === null) return null;
    if (!data.profile) return {
      ...data, status, view: undefined,
      familyError: data.familyError ?? (status.lastSyncedAt ? "Family settings haven't loaded" : null),
    };
    const profile = { ...data.profile, defaultCategoryId: data.profile.defaultCategoryId ?? null, loaded: true, resolved: true };
    const period = periodBounds(now, profile.cycleStartDay, profile.timezone);
    return { ...data, status, view: computeStandings([period], data.categories, data.expenses, data.budgets, [], profile)[0] };
  }, [data, now, status]);
}
